import { type UseFormReturn, FormProvider } from 'react-hook-form';

import { VoucherFormDebugPanel } from '../components/debug/voucher-form-debug-panel';
import { VoucherInformationForm } from '../components/forms/voucher-information-form';
import { VoucherProductForm } from '../components/forms/voucher-product-form';
import { VoucherTargetForm } from '../components/forms/voucher-target-form';
import { VoucherUsageForm } from '../components/forms/voucher-usage-form';
import { VoucherFormLayout } from '../components/layouts/voucher-form-layout';
import { VoucherPreview } from '../components/voucher-preview';
import { useVoucherCreate } from '../hooks';
import type { CreateVoucherRequest } from '../types';

const STEP_COMPONENTS: ReadonlyArray<
  (props: { form: UseFormReturn<CreateVoucherRequest> }) => JSX.Element
> = [
  ({ form }) => <VoucherInformationForm form={form} />,
  ({ form }) => <VoucherTargetForm form={form} />,
  ({ form }) => <VoucherUsageForm form={form} />,
  ({ form }) => <VoucherProductForm form={form} />,
];

const renderStep = (
  stepIndex: number,
  form: UseFormReturn<CreateVoucherRequest>
) => {
  const Component = STEP_COMPONENTS[stepIndex] ?? STEP_COMPONENTS[0];
  return <Component form={form} />;
};

export function VoucherCreatePage() {
  const {
    form,
    currentStep,
    finalStepIndex,
    handleNextStep,
    handlePreviousStep,
    handleStepChange,
    isSubmitting,
  } = useVoucherCreate();

  // Hide debug panel in production (api-v2.sukandaonelink.com)
  const isProduction =
    process.env['NX_API_URL']?.includes('api-v2.sukandaonelink.com') ?? false;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleNextStep();
        }}
      >
        <VoucherFormLayout
          currentStep={currentStep}
          handleNext={() => void handleNextStep()}
          handleBack={handlePreviousStep}
          preview={<VoucherPreview form={form} />}
          finalStepIndex={finalStepIndex}
          isNextLoading={isSubmitting}
          onStepChange={(stepIndex) => void handleStepChange(stepIndex)}
          enableStepNavigation
        >
          {renderStep(currentStep, form)}
        </VoucherFormLayout>
        {!isProduction && (
          <VoucherFormDebugPanel form={form} currentStep={currentStep} />
        )}
      </form>
    </FormProvider>
  );
}
